import React, { useState, useMemo, useContext, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useResignation } from "../../contexts/ResignationContext";
import Modal from "../../components/UI/Modal";
import ResignationForm from "../../components/Form/ResignationForm";
import ReusableTable from "../../components/UI/ReusableTable";
import "react-loading-skeleton/dist/skeleton.css";
import { FaExchangeAlt } from "react-icons/fa";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { MoreVertical, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import StatusBadge from '../../components/UI/StatusBadge';

// Custom Alert Dialog Component
const AlertDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-black p-6 rounded-t-lg md:rounded-lg shadow-lg w-full max-w-sm mx-auto transition-transform duration-300">
        <h2 className="text-lg font-semibold text-center dark:text-white">Are you sure?</h2>
        <p className="text-gray-600 mt-2 text-center dark:text-gray-500">This action cannot be undone.</p>
        <div className="mt-4 flex justify-between space-x-3">
          <button
            className="block w-full py-2 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-10 py-2 text-white bg-red-600 rounded hover:bg-red-700 w-full"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Resignation = React.memo(() => {
  const { user, roles } = useContext(AuthContext);
  const isManager = roles.includes("Manager");
  const { resignations, fetchResignations, addResignation, updateResignation, deleteResignation, takeActionOnResignation, pagination, loading } = useResignation(); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingResignation, setEditingResignation] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionComments, setActionComments] = useState("");
  const [actionResignationId, setActionResignationId] = useState(null);

  // console.log("pagination", pagination);

  const handleDeleteResignation = async (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingResignation(null);
  };

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (resignation) => {
      try {
        if (editingResignation) {
          // console.log("resignation payload", resignation);
        
          // Convert FormData to object
          // console.log(resignation instanceof FormData)
          const resignationData = resignation instanceof FormData ? convertFormDataToObject(resignation) : resignation;
  
          // console.log("Payload being sent:", { ...editingResignation, ...resignationData });
  
          const result = await updateResignation({ ...editingResignation, ...resignationData });
          if (result?.ok) {
            setNotification({ message: result.message, type: "success" });
          } else {
            setNotification({ message: result?.message || "Failed to update resignation.", type: "error" });
            return;
          }
        } else {
          // console.log("create resignation", resignation)
          const result = await addResignation(resignation);
          if (result?.ok) {
            setNotification({ message: result.message, type: "success" });
            if (result.redirectToFirstPage) {
              setCurrentPage(1);
              fetchResignations(1);
            }
          } else {
            setNotification({ message: result?.message || "Failed to add resignation.", type: "error" });
            return;
          }
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          // setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
      }
    },
    [editingResignation, addResignation, updateResignation, closeModal]
  );

  // const confirmDelete = async () => {
  //   if (!deleteId) return;
  //   try {
  //     await deleteResignation(deleteId);
  //     setNotification({message: "Resignation deleted successfully", type: "success"});
  //   } catch (error) {
  //     setNotification({message: "Failed to delete resignation", type: "error"});
  //   } finally {
  //     setShowDeleteDialog(false);
  //     setDeleteId(null);
  //   }
  // };

  // Add the missing confirmDelete function
  const confirmDelete = async () => {
    if (deleteId) {
      try {
        const result = await deleteResignation(deleteId);
        if (result?.ok) {
          setNotification({ message: result.message, type: "success" });
        } else {
          setNotification({ message: result?.message || "Failed to delete resignation.", type: "error" });
        }
        // The context will automatically call fetchResignations() to refresh the data
      } catch (error) {
        console.error('Failed to delete resignation:', error);
        // Optionally show an error toast/notification here
      } finally {
        setShowDeleteDialog(false);
        setDeleteId(null);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteId(null);
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest('.dropdown-container')) {
      setOpenDropdownId(null);
    }
  };

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 200;
      const dropdownHeight = 180;
      let x = rect.right - dropdownWidth;
      let y = rect.bottom + 4;
      if (x < 0) x = rect.left - dropdownWidth;
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4;
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      if (x < 0) x = 8;
      if (y < 0) y = 8;
      setDropdownCoords({ x, y });
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Memoized Columns Definition
  const columns = useMemo(
    () => [
      { label: "Employee ID", field: "employee_id", type: "number" },
      { label: "Resignation Date", field: "resignation_date", type: "date" },
      { label: "Last Working Day", field: "last_working_day", type: "date" },
      { label: "Notice Period Days", field: "notice_period_days", type: "text" },
      { label: "Status", field: "status", type: "text", 
        render: (row) => (
          <StatusBadge status={row?.status} />
        )
       },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            {(row.status !== "approved" && row.status !== "rejected") && (
              <div className="relative dropdown-container">
                <button
                  ref={(el) => {
                    if (el) buttonRefs.current[row.employee_id] = el;
                  }}
                  onClick={(e) => handleDropdownToggle(row.employee_id, e)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            )}
            {openDropdownId === row.employee_id && (row.status !== "approved" && row.status !== "rejected") &&
              createPortal(
                <div
                  style={{
                    position: "fixed",
                    top: dropdownCoords.y,
                    left: dropdownCoords.x,
                    zIndex: 9999,
                  }}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[200px]"
                >
                  <div className="py-1 flex flex-col space-y-0">
                    {/* Edit when submitted or acknowledged */}
                    {(row.status === "submitted" || row.status === "acknowledged") && (
                      <button
                        onClick={() => {
                          openModalForEdit(row);
                          setOpenDropdownId(null);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                      >
                        Edit
                      </button>
                    )}
                    {/* Delete when submitted or acknowledged or withdrawn */}
                    {(row.status === "submitted" || row.status === "acknowledged" || row.status === "withdrawn") && (
                      <button
                        className="text-left text-red-600 text-sm hover:text-red-800 px-3 py-2"
                        onClick={() => {
                          handleDeleteResignation(row.employee_id);
                          setOpenDropdownId(null);
                        }}
                      >
                        Delete
                      </button>
                    )}                      

                    {/* Approve/Reject */}
                    {row.status === "submitted" && (
                      <>
                        <button
                          onClick={() => openActionModal(row.employee_id, "approve")}
                          className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors block"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openActionModal(row.employee_id, "reject")}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors block"
                        >
                          Reject
                        </button>
                        {/* <button
                          onClick={() => openActionModal(row.employee_id, "acknowledge")}
                          className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                        >
                          Acknowledge
                        </button> */}
                        <button
                          onClick={() => openActionModal(row.employee_id, "withdraw")}
                          className="w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors block"
                        >
                          Withdraw
                        </button>
                      </>
                    )}
                  </div>
                </div>,
                document.body
              )}
          </div>
        ),
      },
    ],
    [roles, isManager, user, openDropdownId, dropdownCoords]
  );

  const openActionModal = (employee_id, action) => {
    setActionResignationId(employee_id); // Use employee_id instead of id
    setActionType(action);
    setActionComments("");
    setIsActionModalOpen(true);
  };

  const closeActionModal = () => {
    setIsActionModalOpen(false);
    setActionType(null);
    setActionComments("");
    setActionResignationId(null);
  };

  const handleActionSubmit = async () => {
    if (!actionComments.trim()) {
      setNotification({
        message: "Comments are required to proceed.",
        type: "error",
      });
      return;
    }

    try {
      const result = await takeActionOnResignation(actionResignationId, {
        action: actionType,
        hr_comments: actionComments.trim(),
      });
      if (result?.ok) {
        setNotification({ message: result.message, type: "success" });
        closeActionModal();
      } else {
        setNotification({ message: result?.message || `Failed to ${actionType} resignation.`, type: "error" });
      }
    } catch (error) {
      setNotification({
        message: `Failed to ${actionType} resignation.`,
        type: "error",
      });
    }
  };

  const openModalForAdd = () => {
    setEditingResignation(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (resignation) => {
    setEditingResignation(resignation);
    setIsModalOpen(true);
  };

  // Memoized Exclude Filter Columns
  const excludeFilterColumns = useMemo(() => ["actions", "job_description", "justification"], []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchResignations(newPage);
    }
  };
  
  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-950 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Resignations</h2>
        {/* {isManager && ( */}
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto">
            Add Resignation
          </button>
        {/* )} */}
      </div>

      {/* data table */}
      <div>
        <ReusableTable
          data={resignations}
          loading={loading}
          columns={columns}
          filterable={true}
          excludeFilterColumns={excludeFilterColumns}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
        />

        {/* Pagination */}
        {!isFiltering && resignations?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingResignation ? "Edit Resignation" : "Add New Resignation"}
        fullSize={false}>
        <ResignationForm
          closeModal={closeModal}
          request={editingResignation}
          onSubmit={handleFormSubmit}
          isEditing={Boolean(editingResignation)}
          setNotification={setNotification}
          initialValues={editingResignation || { employee_id: "", resignation_date: "", last_working_day: "", notice_period_days: "", reason: "" }}
        />
      </Modal>

      {/* Modal for Approve/Reject Action */}
      <Modal
        isOpen={isActionModalOpen}
        onRequestClose={closeActionModal}
        title={`${actionType ? (actionType.charAt(0).toUpperCase() + actionType.slice(1)) : ""} Resignation`}
        fullSize={false}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Comments <span className="text-red-500">*</span>
          </label>
          <textarea
            value={actionComments}
            onChange={e => setActionComments(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={`Please provide comments for ${actionType}ing this resignation...`}
            required
          />
          <div className="flex justify-end items-center gap-3 mt-6">
            <button
              type="button"
              onClick={closeActionModal}
              className="px-12 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md0"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleActionSubmit}
              className={`px-4 py-2 text-white ${actionType === "approve" ? "bg-green-600 hover:bg-green-700" : actionType === "reject" ? "bg-red-600 hover:bg-red-700" : actionType === "acknowledge" ? "bg-blue-600 hover:bg-blue-700" : "bg-amber-600 hover:bg-amber-700"} rounded-md`}
            >
              {actionType === "approve" ? "Approve" : actionType === "reject" ? "Reject" : actionType === "acknowledge" ? "Acknowledge" : "Withdraw"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Hiring Request"
        message="Are you sure you want to delete this hiring request? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
});

export default Resignation;
